import {
  ChangelogEntry,
  useChangelogs,
  useLatestChangelog,
  useSetChangelogViewed,
} from '@renderer/windows/features/changelogs/atoms'
import type { Components as MarkdownComponents } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { Video } from '@renderer/windows/features/changelogs/components/Video'
import { useEffect } from 'react'
import { Modal, useModalContext } from '@renderer/components/Modal'
import { FadingScrollable } from '@renderer/chat/components/elements/FadingScrollable'
import { twMerge } from 'tailwind-merge'
import { LoadingState, Markdown } from '@highlight/ui'
import { useAppVersion } from '@renderer/hooks/useAppVersion'

// Changelog bodies embed raw `<video>` tags, which rehype-raw parses and `Video` renders.
const Components: MarkdownComponents = { video: Video }

const RAW_HTML = [rehypeRaw]

export const ChangelogModal = () => {
  const { context } = useModalContext('changelog')
  const useLatest = !!context?.useLatest
  const latestChangelog = useLatestChangelog()
  const changelogs = useChangelogs()
  const setChangelogViewed = useSetChangelogViewed()

  useEffect(() => {
    if (!latestChangelog) {
      return
    }
    setChangelogViewed(latestChangelog.id)
  }, [latestChangelog, setChangelogViewed])

  const entries = useLatest && latestChangelog ? [latestChangelog] : changelogs

  return (
    <Modal>
      <Modal.Content className="w-[min(720px,calc(100%-2rem))]" initialFocus={false}>
        <Modal.Header className="pb-3">
          <Modal.Title>What's new</Modal.Title>
        </Modal.Header>
        {/* Fixed height, not max: entries load after the modal opens and would otherwise grow it. */}
        <Modal.Body
          className="h-[min(760px,calc(100vh-8rem))] max-h-none"
          contentClassName="flex flex-col p-0 group-last/body:pb-0"
        >
          {entries?.length ? (
            <FadingScrollable direction="bottom" className="min-h-0 flex-1 space-y-10 px-6 pb-6">
              {entries.map((changelog, index) => (
                <Changelog key={changelog.id} {...changelog} index={index} />
              ))}
            </FadingScrollable>
          ) : (
            <LoadingState role="status" className="text-foreground-secondary flex-1 justify-center pb-6">
              <LoadingState.Spinner />
              <span>Getting the latest updates</span>
            </LoadingState>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal>
  )
}

const Changelog = (changelog: ChangelogEntry & { index?: number }) => {
  const { version, semver } = useAppVersion()
  const versionsText = changelog.versions?.join(', ')
  const headerText = changelog.title?.length
    ? changelog.title
    : changelog.versions?.includes(version)
      ? `New in ${semver?.major}.${semver?.minor}.${semver?.patch} ✨`
      : versionsText
        ? `Previously in ${versionsText} 📝`
        : 'Previously 📝'

  return (
    <div className="flex flex-col">
      <div className="bg-background-overlay sticky left-0 top-0 z-[1] flex h-10 items-center gap-3 border-b leading-normal">
        <span
          className={twMerge(
            'min-w-0 flex-1 truncate',
            changelog.index === 0 || changelog.index === undefined
              ? 'text-foreground font-semibold'
              : 'text-foreground-placeholder font-medium',
          )}
        >
          {headerText}
        </span>
        {changelog.published_at && (
          <span className="text-foreground-placeholder shrink-0 text-sm font-normal tabular-nums">
            {new Date(changelog.published_at).toLocaleDateString()}
          </span>
        )}
      </div>
      <Markdown
        key={changelog.id}
        rehypePlugins={RAW_HTML}
        components={Components}
        ladder="compact"
        className="py-4 [&_hr+*]:mt-[var(--md-rule)] [&_iframe]:aspect-video [&_iframe]:w-full [&_iframe]:rounded-lg [&_img]:w-full [&_video]:rounded-lg"
      >
        {changelog?.body}
      </Markdown>
    </div>
  )
}
